import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FileText, Loader2, Pencil, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { type ColumnDef, DataTable, DataTableColumnHeader } from "@/components/ui/data-table";
import {
	ConfirmDialog,
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { badgeBase, statusClasses } from "@/lib/colors";
import { useDebounce } from "@/lib/use-debounce";
import { cn } from "@/lib/utils";
import {
	type Contact,
	chatSearch,
	formatDateTime,
	formatSize,
	helpdeskApi,
	type Template,
	type TemplateKey,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";
import { ContactLabelButton } from "./contact-label";
import { OfficeDeviceButton } from "./office-device";
import { PairingQueue, UnpairButton } from "./pairing";
import { RunsPanel } from "./runs-panel";
import { useTableExport } from "./use-table-export";

export const MANAGE_TABS = [
	"parowanie",
	"kontakty",
	"ksiazka",
	"szablony",
	"zalaczniki",
	"zadania",
] as const;
export type ManageTab = (typeof MANAGE_TABS)[number];

const TAB_LABEL: Record<ManageTab, string> = {
	parowanie: "Parowanie",
	kontakty: "Kontakty",
	ksiazka: "Książka adresowa",
	szablony: "Szablony",
	zalaczniki: "Stałe załączniki",
	zadania: "Zadania w tle",
};

export function ManagePage({
	tab,
	onTabChange,
}: {
	tab: ManageTab;
	onTabChange: (tab: ManageTab) => void;
}) {
	return (
		<div className="flex h-full flex-col gap-3 p-3 sm:p-4">
			<PageHeader
				title="Helpdesk — zarządzanie"
				description="Parowanie adresów, kontakty, książka adresowa, szablony maili i zadania w tle."
			/>
			<Tabs
				value={tab}
				onValueChange={(v) => onTabChange(v as ManageTab)}
				className="min-h-0 flex-1"
			>
				{/* Telefon: zakładki przewijają się w bok, nie rozpychają strony. */}
				<TabsList className="max-w-full justify-start overflow-x-auto">
					{MANAGE_TABS.map((t) => (
						<TabsTrigger key={t} value={t}>
							{TAB_LABEL[t]}
						</TabsTrigger>
					))}
				</TabsList>
				<TabsContent value="parowanie" className="overflow-y-auto">
					<PairingQueue />
				</TabsContent>
				<TabsContent value="kontakty" className="flex min-h-0 flex-col">
					<ContactsTab />
				</TabsContent>
				<TabsContent value="ksiazka" className="flex min-h-0 flex-col">
					<ContactsTab addressBook />
				</TabsContent>
				<TabsContent value="szablony" className="overflow-y-auto">
					<TemplatesTab />
				</TabsContent>
				<TabsContent value="zalaczniki" className="overflow-y-auto">
					<StandardAttachmentsTab />
				</TabsContent>
				<TabsContent value="zadania" className="overflow-y-auto">
					<RunsPanel />
				</TabsContent>
			</Tabs>
		</div>
	);
}

// ── Kontakty ─────────────────────────────────────────────────────────────────

const SOURCE_LABEL: Record<Contact["source"], string> = {
	mail: "mail",
	otrs: "OTRS",
	manual: "ręcznie",
};

// Stała poza komponentem, jak w skrzynce.
const CONTACT_COLUMN_LABELS = {
	email: "Adres",
	name: "Imię i nazwisko",
	phone: "Telefon",
	idfirms: "Apteki",
	status: "Stan",
	source: "Źródło",
};

function EditContactDialog({
	contact,
	onOpenChange,
}: {
	contact: Contact | null;
	onOpenChange: (open: boolean) => void;
}) {
	const isNew = contact?.email === "";
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [phone, setPhone] = useState("");
	const [inBook, setInBook] = useState(false);
	useEffect(() => {
		setEmail(contact?.email ?? "");
		setName(contact?.name ?? "");
		setPhone(contact?.phone ?? "");
		setInBook(contact?.inAddressBook ?? false);
	}, [contact]);
	const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	const save = useHelpdeskMutation(
		() =>
			helpdeskApi.saveContact(
				email.trim(),
				// Nowa osoba z adresem znanego nadawcy: puste pola nie kasują jego danych.
				isNew
					? { name: name || undefined, phone: phone || undefined, addressBook: inBook }
					: { name, phone, addressBook: inBook },
			),
		{
			success: "Kontakt zapisany",
		},
	);
	return (
		<Dialog open={!!contact} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-sm">
				<DialogHeader>
					<DialogTitle>{isNew ? "Nowa osoba w książce adresowej" : contact?.email}</DialogTitle>
				</DialogHeader>
				<DialogBody className="space-y-3">
					{isNew ? (
						<div className="space-y-1">
							<Label htmlFor="hd-contact-email" className="text-xs">
								Adres e-mail
							</Label>
							<Input
								id="hd-contact-email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
						</div>
					) : null}
					<div className="space-y-1">
						<Label htmlFor="hd-contact-name" className="text-xs">
							Imię i nazwisko
						</Label>
						<Input id="hd-contact-name" value={name} onChange={(e) => setName(e.target.value)} />
					</div>
					<div className="space-y-1">
						<Label htmlFor="hd-contact-phone" className="text-xs">
							Telefon
						</Label>
						<Input id="hd-contact-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
					</div>
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={inBook} onChange={(e) => setInBook(e.target.checked)} />
						W książce adresowej — podpowiadany w polach Do i DW
					</label>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Anuluj
					</Button>
					<Button
						disabled={save.isPending || !validEmail}
						onClick={() => save.mutate(undefined, { onSuccess: () => onOpenChange(false) })}
					>
						Zapisz
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

const NEW_BOOK_ENTRY: Contact = {
	email: "",
	name: null,
	phone: null,
	source: "manual",
	label: null,
	inAddressBook: true,
	idfirms: [],
	deviceUuid: null,
	deviceName: null,
};
const contactState = (c: Contact) => c.label ?? (c.idfirms.length ? "sparowany" : "do sparowania");
// Kolumny parowania nie dotyczą osób z książki adresowej.
const PAIRING_COLUMNS = new Set(["idfirms", "status", "device", "source"]);

/** Kontakty nadawców albo (`addressBook`) książka adresowa: osoby podpowiadane w Do/DW. */
function ContactsTab({ addressBook = false }: { addressBook?: boolean }) {
	const key = useHelpdeskKey();
	const [q, setQ] = useState("");
	const debounced = useDebounce(q.trim(), 400);
	const { data = [], isLoading } = useQuery({
		queryKey: key("contacts", debounced, addressBook),
		queryFn: () => helpdeskApi.contacts({ q: debounced || undefined, addressBook }),
		staleTime: 60_000,
	});
	const [editing, setEditing] = useState<Contact | null>(null);
	const tableExport = useTableExport();

	const columns = useMemo<ColumnDef<Contact>[]>(
		() => [
			{
				accessorKey: "email",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Adres" />,
				size: 240,
				meta: { mobileRole: "title" },
			},
			{
				accessorKey: "name",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Imię i nazwisko" />,
				size: 180,
				meta: { mobileRole: "subtitle" },
			},
			{
				accessorKey: "phone",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Telefon" />,
				size: 120,
			},
			{
				id: "idfirms",
				accessorFn: (c) => c.idfirms.join(", "),
				header: ({ column }) => <DataTableColumnHeader column={column} title="Apteki" />,
				cell: ({ row }) => (
					<span className="flex flex-wrap gap-1">
						{row.original.idfirms.map((idfirm) => (
							<span
								key={idfirm}
								className="inline-flex items-center gap-0.5 rounded-sm border border-border px-1 font-mono"
							>
								{idfirm}
								<UnpairButton
									email={row.original.email}
									pharmacy={{ idfirm, name: `apteki ${idfirm}` }}
								/>
							</span>
						))}
					</span>
				),
				size: 200,
			},
			{
				id: "status",
				// Etykieta adresu spoza aptek („Biuro”) zastępuje stan parowania.
				accessorFn: contactState,
				header: ({ column }) => <DataTableColumnHeader column={column} title="Stan" />,
				cell: ({ row, getValue }) => (
					<span className="flex items-center gap-1.5">
						<span
							className={cn(
								badgeBase,
								row.original.label
									? statusClasses.info
									: row.original.idfirms.length
										? statusClasses.success
										: statusClasses.warning,
							)}
						>
							{String(getValue())}
						</span>
						{row.original.idfirms.length ? null : (
							<>
								<ContactLabelButton
									email={row.original.email}
									label={row.original.label}
									size="xs"
								/>
								<OfficeDeviceButton
									email={row.original.email}
									deviceUuid={row.original.deviceUuid}
									size="xs"
								/>
								<Link
									to="/helpdesk/czat"
									search={chatSearch({ email: row.original.email })}
									className="text-primary hover:underline"
								>
									rozmowa
								</Link>
							</>
						)}
					</span>
				),
				size: 300,
				meta: {
					facetedOptions: [
						...new Set(["sparowany", "do sparowania", ...data.map(contactState)]),
					].map((v) => ({ label: v, value: v })),
				},
			},
			{
				id: "device",
				// Odwołane albo usunięte urządzenie: parowanie zostaje, oznaczone.
				accessorFn: (c) => (c.deviceUuid ? (c.deviceName ?? "urządzenie odwołane") : ""),
				header: ({ column }) => <DataTableColumnHeader column={column} title="Urządzenie" />,
				cell: ({ row, getValue }) =>
					row.original.deviceUuid && !row.original.deviceName ? (
						<span className={cn(badgeBase, statusClasses.warning)}>{String(getValue())}</span>
					) : (
						String(getValue())
					),
				size: 160,
			},
			{
				id: "source",
				accessorKey: "source",
				header: ({ column }) => <DataTableColumnHeader column={column} title="Źródło" />,
				cell: ({ row }) => SOURCE_LABEL[row.original.source] ?? row.original.source,
				size: 90,
			},
			{
				id: "edit",
				header: "",
				cell: ({ row }) => (
					<Button
						size="icon"
						variant="ghost"
						className="size-6"
						aria-label={`Edytuj ${row.original.email}`}
						onClick={() => setEditing(row.original)}
					>
						<Pencil className="size-3" />
					</Button>
				),
				size: 40,
				enableSorting: false,
				enableColumnFilter: false,
				meta: { alwaysLast: true, mobileRole: "actions" },
			},
		],
		[data],
	);
	const visibleColumns = useMemo(
		() => (addressBook ? columns.filter((c) => !PAIRING_COLUMNS.has(c.id ?? "")) : columns),
		[addressBook, columns],
	);

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-2">
			<div className="flex flex-wrap items-center gap-2">
				<Input
					value={q}
					onChange={(e) => setQ(e.target.value)}
					placeholder="Szukaj adresu, nazwiska…"
					aria-label="Szukaj kontaktu"
					className="h-8 w-72 text-xs"
				/>
				{isLoading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : null}
				{addressBook ? (
					<Button
						size="sm"
						className="ml-auto h-8 text-xs"
						onClick={() => setEditing(NEW_BOOK_ENTRY)}
					>
						Dodaj osobę
					</Button>
				) : null}
				<Button
					size="sm"
					variant="outline"
					className={cn("h-8 text-xs", !addressBook && "ml-auto")}
					disabled={!tableExport.ready || tableExport.exporting}
					onClick={tableExport.run}
				>
					Eksportuj
				</Button>
			</div>
			<div className="flex min-h-[24rem] flex-1 flex-col rounded-md border border-border bg-card">
				<DataTable
					tableId={addressBook ? "helpdesk.address-book" : "helpdesk.contacts"}
					className="min-h-0 w-full flex-1"
					columns={visibleColumns}
					data={data}
					getRowId={(c) => c.email}
					enableToolbar
					enableColumnVisibility
					onExportReady={tableExport.onExportReady}
					exportFileName={addressBook ? "Helpdesk_ksiazka_adresowa" : "Helpdesk_kontakty"}
					columnLabels={CONTACT_COLUMN_LABELS}
				/>
			</div>
			<EditContactDialog contact={editing} onOpenChange={(open) => !open && setEditing(null)} />
		</div>
	);
}

// ── Szablony ─────────────────────────────────────────────────────────────────

const TEMPLATE_LABEL: Record<TemplateKey, { title: string; help: string }> = {
	received: {
		title: "Przyjęte",
		help: "Wysyłany automatycznie przy otwarciu zgłoszenia (nie dla alertów i maili automatycznych).",
	},
	closed: {
		title: "Zakończone",
		help: "Wysyłany przy zakończeniu przyciskiem albo notatką, chyba że zaznaczysz „nie wysyłaj”. Odpowiedź do zgłaszającego z „Zakończ po wysłaniu” dostaje zamiast niego dopisek.",
	},
	closed_footer: {
		title: "Dopisek o zakończeniu",
		help: "Dopisywany pod odpowiedzią do zgłaszającego z „Zakończ po wysłaniu” zamiast osobnego maila, gdy szablon „Zakończone” jest włączony. Pierwsza linia pogrubiona.",
	},
	signature: { title: "Podpis", help: "Wstawiany na końcu każdej odpowiedzi mailem." },
};

/** Placeholdery szablonów (serwer podstawia je przy wysyłce) z opisem do podpowiedzi. */
export const TEMPLATE_PLACEHOLDERS: Record<string, string> = {
	numer: "numer zgłoszenia",
	temat: "temat zgłoszenia",
	nadawca: "imię albo adres nadawcy",
	tresc: "pierwsza wiadomość apteki",
	agent: "imię i nazwisko zalogowanego administratora (podpis odpowiedzi)",
	apteka: "nazwa apteki",
};

function TemplateEditor({ template }: { template: Template }) {
	const [subject, setSubject] = useState(template.subject);
	const [body, setBody] = useState(template.body);
	const [enabled, setEnabled] = useState(template.enabled);
	const [previewNumber, setPreviewNumber] = useState("");
	const bodyRef = useRef<HTMLTextAreaElement>(null);
	useEffect(() => {
		setSubject(template.subject);
		setBody(template.body);
		setEnabled(template.enabled);
	}, [template]);
	const key = useHelpdeskKey();
	const draft = useDebounce({ subject, body, number: previewNumber }, 500);
	const preview = useQuery({
		queryKey: key("template-preview", template.key, draft),
		queryFn: () =>
			helpdeskApi.previewTemplate({
				key: template.key,
				subject: draft.subject,
				body: draft.body,
				number: /^\d+$/.test(draft.number) ? Number(draft.number) : undefined,
			}),
		staleTime: 60_000,
	});
	const save = useHelpdeskMutation(
		() => helpdeskApi.saveTemplate(template.key, { subject, body, enabled }),
		{
			success: "Szablon zapisany",
			error: "Nie udało się zapisać szablonu",
		},
	);
	const dirty =
		subject !== template.subject || body !== template.body || enabled !== template.enabled;
	// Podpis i dopisek nie są osobnym mailem; dopisek włącza i wyłącza szablon „Zakończone”.
	const ownMail = template.key === "received" || template.key === "closed";

	const insert = (name: string) => {
		const el = bodyRef.current;
		const token = `{{${name}}}`;
		if (!el) return setBody((b) => b + token);
		const { selectionStart: s, selectionEnd: e } = el;
		setBody(body.slice(0, s) + token + body.slice(e));
	};

	return (
		<div className="grid gap-3 lg:grid-cols-2">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground">{TEMPLATE_LABEL[template.key].help}</p>
				{template.key !== "closed_footer" ? (
					<label className="flex items-center gap-2 text-sm">
						<input
							type="checkbox"
							checked={enabled}
							onChange={(e) => setEnabled(e.target.checked)}
						/>
						Włączony
					</label>
				) : null}
				{ownMail ? (
					<div className="space-y-1">
						<Label htmlFor={`hd-tpl-subject-${template.key}`} className="text-xs">
							Temat
						</Label>
						<Input
							id={`hd-tpl-subject-${template.key}`}
							value={subject}
							onChange={(e) => setSubject(e.target.value)}
							className="h-8 text-sm"
						/>
					</div>
				) : null}
				<div className="space-y-1">
					<Label htmlFor={`hd-tpl-body-${template.key}`} className="text-xs">
						Treść
					</Label>
					<textarea
						id={`hd-tpl-body-${template.key}`}
						ref={bodyRef}
						value={body}
						onChange={(e) => setBody(e.target.value)}
						className="min-h-56 w-full resize-y rounded-md border border-input bg-background p-2 font-mono text-xs"
					/>
				</div>
				<div className="flex flex-wrap items-center gap-1 text-xs">
					<span className="text-muted-foreground">Wstaw:</span>
					{Object.entries(TEMPLATE_PLACEHOLDERS).map(([p, hint]) => (
						<button
							key={p}
							type="button"
							title={hint}
							className="rounded-sm border border-border px-1.5 font-mono hover:bg-muted"
							onClick={() => insert(p)}
						>
							{`{{${p}}}`}
						</button>
					))}
				</div>
				<div className="flex items-center gap-2">
					<span className="text-xs text-muted-foreground">
						Zmieniony {formatDateTime(template.updatedAt)}
					</span>
					<Button
						size="sm"
						className="ml-auto"
						disabled={!dirty || save.isPending}
						onClick={() => save.mutate(undefined)}
					>
						Zapisz szablon
					</Button>
				</div>
			</div>
			<div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
				<div className="flex items-center gap-2 text-xs">
					<span className="font-medium">Podgląd</span>
					<Input
						value={previewNumber}
						onChange={(e) => setPreviewNumber(e.target.value.trim())}
						placeholder="nr zgłoszenia (opcjonalnie)"
						aria-label="Numer zgłoszenia do podglądu"
						className="ml-auto h-7 w-44 text-xs"
					/>
					{preview.isFetching ? <Loader2 className="size-3.5 animate-spin" /> : null}
				</div>
				{preview.isError ? (
					<p className="text-xs text-status-error-text">Nie udało się wygenerować podglądu.</p>
				) : preview.data ? (
					<>
						{ownMail ? <p className="text-sm font-medium">{preview.data.subject}</p> : null}
						{preview.data.html ? (
							// Pusty sandbox: bez skryptów i bez dostępu do aplikacji — HTML tak, jak zobaczy go apteka.
							<iframe
								title="Podgląd maila HTML"
								sandbox=""
								srcDoc={preview.data.html}
								className="h-[28rem] w-full rounded-md border border-border bg-white"
							/>
						) : null}
						{preview.data.html ? (
							<p className="text-xs text-muted-foreground">Wersja tekstowa (ta sama treść):</p>
						) : null}
						<p className="whitespace-pre-wrap text-sm">{preview.data.body}</p>
					</>
				) : null}
			</div>
		</div>
	);
}

function TemplatesTab() {
	const key = useHelpdeskKey();
	const { data = [], isLoading } = useQuery({
		queryKey: key("templates"),
		queryFn: helpdeskApi.templates,
	});
	const [current, setCurrent] = useState<TemplateKey>("received");
	const template = data.find((t) => t.key === current);
	if (isLoading) return <p className="text-xs text-muted-foreground">Wczytywanie…</p>;
	return (
		<div className="space-y-3">
			<div className="flex gap-1">
				{(Object.keys(TEMPLATE_LABEL) as TemplateKey[]).map((k) => (
					<Button
						key={k}
						size="sm"
						variant={k === current ? "default" : "outline"}
						onClick={() => setCurrent(k)}
					>
						{TEMPLATE_LABEL[k].title}
					</Button>
				))}
			</div>
			{template ? (
				<TemplateEditor template={template} />
			) : (
				<p className="text-xs text-muted-foreground">Brak szablonu.</p>
			)}
		</div>
	);
}

// ── Stałe załączniki ─────────────────────────────────────────────────────────

function StandardAttachmentsTab() {
	const key = useHelpdeskKey();
	const { data = [] } = useQuery({
		queryKey: key("standard-attachments"),
		queryFn: helpdeskApi.standardAttachments,
	});
	const [name, setName] = useState("");
	const [file, setFile] = useState<File | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);
	const [deleting, setDeleting] = useState<{ id: number; name: string } | null>(null);
	const add = useHelpdeskMutation(
		() => helpdeskApi.addStandardAttachment(file as File, name.trim() || (file as File).name),
		{
			success: "Załącznik dodany",
			error: "Nie udało się dodać załącznika",
		},
	);
	const remove = useHelpdeskMutation((id: number) => helpdeskApi.deleteStandardAttachment(id), {
		success: "Załącznik usunięty",
	});
	return (
		<div className="max-w-3xl space-y-3">
			<p className="text-xs text-muted-foreground">
				Instrukcje (PDF) do dołączania jednym kliknięciem w odpowiedzi mailem.
			</p>
			<div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3">
				<div className="space-y-1">
					<Label htmlFor="hd-std-name" className="text-xs">
						Nazwa
					</Label>
					<Input
						id="hd-std-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="np. Instrukcja drukarki"
						className="h-8 w-64 text-sm"
					/>
				</div>
				<input
					ref={fileRef}
					type="file"
					className="hidden"
					aria-label="Plik załącznika"
					onChange={(e) => setFile(e.target.files?.[0] ?? null)}
				/>
				<Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
					<Upload className="mr-1.5 size-4" />
					{file ? file.name : "Wybierz plik"}
				</Button>
				<Button
					size="sm"
					disabled={!file || add.isPending}
					onClick={() =>
						add.mutate(undefined, {
							onSuccess: () => {
								setFile(null);
								setName("");
								if (fileRef.current) fileRef.current.value = "";
							},
						})
					}
				>
					Dodaj
				</Button>
			</div>
			<ul className="divide-y divide-border rounded-md border border-border bg-card">
				{data.map((a) => (
					<li key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm">
						<FileText className="size-4 text-muted-foreground" />
						<span className="font-medium">{a.name}</span>
						<span className="truncate text-xs text-muted-foreground">
							{a.filename} · {formatSize(a.sizeBytes)}
						</span>
						<Button
							size="icon"
							variant="ghost"
							className="ml-auto size-7"
							aria-label={`Usuń ${a.name}`}
							onClick={() => setDeleting(a)}
						>
							<Trash2 className="size-3.5" />
						</Button>
					</li>
				))}
				{data.length === 0 ? (
					<li className="px-3 py-2 text-xs text-muted-foreground">Brak stałych załączników.</li>
				) : null}
			</ul>
			<ConfirmDialog
				open={!!deleting}
				onOpenChange={(open) => !open && setDeleting(null)}
				title="Usunąć stały załącznik?"
				description={
					deleting
						? `„${deleting.name}” zniknie z listy do wstawiania. Wysłane maile się nie zmienią.`
						: undefined
				}
				variant="destructive"
				confirmLabel="Usuń"
				isLoading={remove.isPending}
				onConfirm={() =>
					deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
				}
			/>
		</div>
	);
}
